import { Box, Flex, Heading } from '@chakra-ui/react';
import React, { useCallback, useMemo, useState } from 'react';

import { useGetInvoicesByOrganizationIdQuery } from '../requests/invoices.gql.generated';
import { CreateInvoiceButton } from './create/CreateInvoiceButton';
import { InvoicesOverviewCards } from './InvoicesOverviewCards';
import { InvoicesTable } from './InvoicesTable';
import {
  filterInvoicesForDatesAndCreditFacilities,
  filterInvoicesForSelectedComputedStatus,
  getInvoicesWithComputedStatus,
  HighlightComputedStatus,
  getInvoiceOverviewInitialDateRange,
} from './utils';
import { IsolatedMultiSelect } from 'packages/common-frontend/components/MultiSelect';
import { ChartDateRange } from 'packages/loanProduct/ui/receivablesDashboard/utils';
import { PortfolioChartDatePicker } from 'packages/portfolio/ui/shared/PortfolioChartDatePicker';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';

export const InvoicesOverview = () => {
  const { user } = useUserAuthOrThrow();
  const { facilities } = useCreditFacilitiesOrThrow();

  const creditFacilityIdsAndOptions = useMemo(() => {
    const creditFacilityIds: string[] = [];
    const creditFacilityOptions: { value: string; label: string }[] = [];

    facilities.forEach((facility) => {
      creditFacilityIds.push(facility.id);
      creditFacilityOptions.push({ value: facility.id, label: facility.name });
    });

    return {
      creditFacilityIds,
      creditFacilityOptions,
    };
  }, [facilities]);

  const initialDateRange = useMemo(
    () => getInvoiceOverviewInitialDateRange(),
    [],
  );

  const [
    selectedComputedStatus,
    setSelectedComputedStatus,
  ] = useState<HighlightComputedStatus | null>(null);
  const [selectedDateRange, setSelectedDateRange] = useState<ChartDateRange>(
    initialDateRange,
  );
  const [selectedCreditFacilityIds, setSelectedCreditFacilityIds] = useState<
    string[]
  >(creditFacilityIdsAndOptions.creditFacilityIds);

  const onSelectDateRange: (
    newDateRange: [Date | null, Date | null],
  ) => void = useCallback(
    (newDateRange: [Date | null, Date | null]) => {
      const [newStartDate, newEndDate] = newDateRange;

      if (newStartDate && newEndDate) {
        setSelectedDateRange({
          chartDataStartDate: newStartDate,
          chartDataEndDate: newEndDate,
        });
      }

      // if dates cleared, pass in the initial date range conditions
      if (!newStartDate && !newEndDate) {
        setSelectedDateRange({
          chartDataStartDate: initialDateRange.chartDataStartDate,
          chartDataEndDate: initialDateRange.chartDataEndDate,
        });
      }
    },
    [initialDateRange],
  );

  const {
    data: invoicesData,
    loading: isInvoicesLoading,
  } = useGetInvoicesByOrganizationIdQuery({
    variables: { organizationId: user.organizationId },
    fetchPolicy: 'no-cache',
  });

  // no filters applied. used for the payments side drawer
  const allInvoicesWithComputedStatus = useMemo(() => {
    return getInvoicesWithComputedStatus({
      invoices: invoicesData?.getInvoicesByOrganizationId,
    });
  }, [invoicesData?.getInvoicesByOrganizationId]);

  // filtered by date and facility filters, used for the highlight status cards
  const filteredInvoicesForDateAndFacility = useMemo(() => {
    return filterInvoicesForDatesAndCreditFacilities({
      invoices: allInvoicesWithComputedStatus,
      dateRange: selectedDateRange,
      selectedCreditFacilityIds,
    });
  }, [
    allInvoicesWithComputedStatus,
    selectedDateRange,
    selectedCreditFacilityIds,
  ]);

  // filtered to a single computed status, activated by selecting a single card and used for overview table
  const invoicesForSelectedComputeStatus = useMemo(() => {
    return filterInvoicesForSelectedComputedStatus({
      invoices: filteredInvoicesForDateAndFacility,
      selectedComputedStatus,
    });
  }, [filteredInvoicesForDateAndFacility, selectedComputedStatus]);

  return (
    <Box>
      <Flex alignItems="center" justifyContent="space-between" mb="40px">
        <Heading display="inline-block">Invoices</Heading>
        <CreateInvoiceButton />
      </Flex>

      <Flex alignItems="center" gap="24px" mb="24px">
        <PortfolioChartDatePicker
          onSelectDateRange={onSelectDateRange}
          currentDateRange={selectedDateRange}
          isClearable={false}
          clearButtonLabel="Reset"
          width="50%"
        />
        <IsolatedMultiSelect
          applyButtonText="Filter"
          buttonProps={{
            width: '50%',
          }}
          counterLabel="Credit facilities"
          initialSelections={selectedCreditFacilityIds}
          isSearchable
          menuListProps={{ zIndex: 'dropdown' }}
          onApplyOptions={setSelectedCreditFacilityIds}
          options={creditFacilityIdsAndOptions.creditFacilityOptions}
          placeholderText="Select credit facilities"
          showSelectedCount
          titleText="All credit facilities"
        />
      </Flex>

      <InvoicesOverviewCards
        invoices={filteredInvoicesForDateAndFacility} // cards will always show aggregates for all invoices
        isLoading={isInvoicesLoading}
        selectedComputedStatus={selectedComputedStatus}
        setSelectedComputedStatus={setSelectedComputedStatus}
      />

      <InvoicesTable
        invoices={invoicesForSelectedComputeStatus}
        isLoading={isInvoicesLoading}
      />
    </Box>
  );
};
